import type { ApiBlockListModel, MemberTypeBackgroundPropertiesModel } from '@/services/platform/api/model';

export interface MemberTheme {
  pageBg: string;
  cardBg: string;
  cardAccent: string;
  iconColor: string;
  contentColor: string;
  logoVariant: 'white' | 'black';
}

interface ApprovedColor {
  value?: string;
  label?: string;
}

interface MemberTypeProperties {
  approvedColor?: ApprovedColor | null;
}

const THEMES: Record<string, MemberTheme> = {
  'member type neutral': {
    pageBg: 'var(--color-jncquoi-neutral-50)',
    cardBg: 'var(--color-jncquoi-neutral-1000)',
    cardAccent: 'var(--color-jncquoi-club-465c-400)',
    iconColor: 'var(--color-jncquoi-neutral-1000)',
    contentColor: 'var(--color-jncquoi-neutral-00)',
    logoVariant: 'white',
  },
  'member type green': {
    pageBg: 'var(--color-jncquoi-club-7720c-50)',
    cardBg: 'var(--color-jncquoi-club-7720c-600)',
    cardAccent: 'var(--color-jncquoi-club-465c-300)',
    iconColor: 'var(--color-jncquoi-club-7720c-600)',
    contentColor: 'var(--color-jncquoi-neutral-00)',
    logoVariant: 'white',
  },
  'member type beige': {
    pageBg: 'var(--color-jncquoi-club-465c-50)',
    cardBg: 'var(--color-jncquoi-club-465c-100)',
    cardAccent: 'var(--color-jncquoi-club-465c-900)',
    iconColor: 'var(--color-jncquoi-club-465c-900)',
    contentColor: 'var(--color-jncquoi-neutral-1000)',
    logoVariant: 'black',
  },
};

export const DEFAULT_MEMBER_THEME: MemberTheme = THEMES['member type neutral'];

export function resolveThemeByLabel(label: string | null | undefined): MemberTheme {
  if (!label) return DEFAULT_MEMBER_THEME;
  return THEMES[label.toLowerCase()] ?? DEFAULT_MEMBER_THEME;
}

export function resolveMemberTheme(
  memberType: string | null | undefined,
  memberTypeBackgrounds: ApiBlockListModel | undefined
): MemberTheme {
  if (!memberType || !memberTypeBackgrounds?.items?.length) {
    return DEFAULT_MEMBER_THEME;
  }

  const normalised = memberType.toLowerCase();

  const match = memberTypeBackgrounds.items.find(item => {
    const props = item?.content?.properties as MemberTypeBackgroundPropertiesModel | undefined;
    const ref = props?.memberType?.[0];
    return ref?.name?.toLowerCase() === normalised;
  });

  if (!match) {
    return DEFAULT_MEMBER_THEME;
  }

  const props = match.content?.properties as MemberTypeBackgroundPropertiesModel | undefined;
  const memberTypeRef = props?.memberType?.[0];
  const memberTypeProps = memberTypeRef?.properties as MemberTypeProperties | undefined;
  const label = memberTypeProps?.approvedColor?.label ?? '';
  const theme = THEMES[label.toLowerCase()];

  return theme ?? DEFAULT_MEMBER_THEME;
}
